/** Renders an optional "Industry Certifications" block. The wrapper fetches
 *  bodies filtered by `{layer, orgType, vendorTrack}` and gives the user
 *  a minimal add-row / list-remove UI. Emits a normalized list of
 *  `[{body_code, member_id, secondary_field}, …]` upstream via `onChange`.
 *
 *  Kept dumb-simple:
 *   · No global state.
 *   · No fancy dropdowns unless there are >1 bodies for the filter.
 *   · Fields are laid out per body's `profile_fields` metadata.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Award, Plus, X } from "lucide-react";

export default function CertificationsPicker({
  layer, orgType, vendorTrack,
  value, onChange,
  testidPrefix = "cert",
}) {
  const [bodies, setBodies] = useState([]);
  const [draft, setDraft]   = useState({ body_code: "", member_id: "", secondary_field: "" });

  useEffect(() => {
    const params = new URLSearchParams();
    if (layer)       params.set("layer",        layer);
    if (orgType)     params.set("org_type",     orgType);
    if (vendorTrack) params.set("vendor_track", vendorTrack);
    api.get(`/meta/industry-bodies?${params.toString()}`)
       .then(({ data }) => {
         setBodies(data.bodies || []);
         if (data.bodies?.length === 1) {
           setDraft((d) => ({ ...d, body_code: data.bodies[0].code }));
         }
       })
       .catch(() => setBodies([]));
  }, [layer, orgType, vendorTrack]);

  const selected = bodies.find((b) => b.code === draft.body_code);
  const [primaryField, secondaryField] = selected?.profile_fields || [];

  const add = () => {
    if (!draft.body_code || !draft.member_id.trim()) return;
    onChange([...(value || []), { ...draft }]);
    setDraft({ body_code: bodies.length === 1 ? bodies[0].code : "", member_id: "", secondary_field: "" });
  };
  const remove = (idx) => {
    const next = [...(value || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  if (bodies.length === 0) return null;   // hide silently for irrelevant flows

  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center gap-2 mb-3">
        <Award size={14} className="text-amber-400" />
        <div className="text-[10px] tracking-[0.25em] uppercase text-white/60">Industry Certifications (Optional)</div>
      </div>

      {/* Existing rows */}
      {value && value.length > 0 && (
        <div className="space-y-1.5 mb-3" data-testid={`${testidPrefix}-list`}>
          {value.map((row, idx) => {
            const body = bodies.find((b) => b.code === row.body_code);
            const rowKey = `${row.body_code}::${row.member_id || ""}::${idx}`;
            return (
              <div key={rowKey}
                   className="flex items-center gap-2 rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5"
                   data-testid={`${testidPrefix}-row-${row.body_code}`}>
                <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-amber-300">{row.body_code}</span>
                <span className="text-xs text-white/85 truncate flex-1" title={row.member_id}>{row.member_id}</span>
                {row.secondary_field && <span className="text-[10px] text-white/45">· {row.secondary_field}</span>}
                <span className="text-[9px] tracking-[0.2em] uppercase text-white/45 ml-auto">{body?.name?.split(" ")[0]}</span>
                <button type="button" onClick={() => remove(idx)}
                        className="text-white/40 hover:text-red-300"
                        aria-label="Remove certification"
                        data-testid={`${testidPrefix}-remove-${idx}`}>
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Adder */}
      <div className="grid sm:grid-cols-3 gap-2">
        {bodies.length > 1 ? (
          <select
            value={draft.body_code}
            onChange={(e) => setDraft((d) => ({ ...d, body_code: e.target.value }))}
            className="bg-black/50 border border-white/15 rounded-md h-9 px-2 text-xs text-white outline-none focus:border-[#F5B82E]/60"
            data-testid={`${testidPrefix}-body-select`}
          >
            <option value="">Select Body…</option>
            {bodies.map((b) => (
              <option key={b.code} value={b.code}>{b.code} · {b.name}</option>
            ))}
          </select>
        ) : (
          <div className="rounded-md border border-white/10 bg-black/30 h-9 flex items-center px-2 text-[11px] tracking-[0.2em] uppercase text-white/60">
            {bodies[0]?.code} · {bodies[0]?.name}
          </div>
        )}
        <input
          type="text"
          value={draft.member_id}
          onChange={(e) => setDraft((d) => ({ ...d, member_id: e.target.value }))}
          placeholder={primaryField || "Member ID"}
          className="bg-black/50 border border-white/15 rounded-md h-9 px-2.5 text-xs text-white placeholder:text-white/35 outline-none focus:border-[#F5B82E]/60"
          data-testid={`${testidPrefix}-member-input`}
        />
        <div className="flex gap-2">
          {secondaryField && (
            <input
              type="text"
              value={draft.secondary_field}
              onChange={(e) => setDraft((d) => ({ ...d, secondary_field: e.target.value }))}
              placeholder={secondaryField}
              className="flex-1 bg-black/50 border border-white/15 rounded-md h-9 px-2.5 text-xs text-white placeholder:text-white/35 outline-none focus:border-[#F5B82E]/60"
              data-testid={`${testidPrefix}-secondary-input`}
            />
          )}
          <button
            type="button"
            onClick={add}
            disabled={!draft.body_code || !draft.member_id.trim()}
            className="h-9 px-3 rounded-md bg-[#F5B82E]/90 hover:bg-[#F5B82E] disabled:opacity-40 disabled:cursor-not-allowed text-black text-[11px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-1"
            data-testid={`${testidPrefix}-add-btn`}
          >
            <Plus size={12} /> Add
          </button>
        </div>
      </div>
    </div>
  );
}
